<template>
  <div class="chart-container">
    <canvas 
      :ref="chartRef" 
      :width="width" 
      :height="height"
      :class="chartClass"
    ></canvas>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js'
import 'chartjs-adapter-date-fns'

// Register Chart.js components
Chart.register(...registerables)

export default {
  name: 'CurveResultsBaseChart',
  // Static chart initialization queue to prevent conflicts
  static: {
    chartInitQueue: [],
    isInitializing: false
  },
  props: {
    // Chart configuration
    chartType: {
      type: String,
      default: 'line',
      validator: (value) => ['line', 'bar', 'scatter', 'radar', 'doughnut', 'pie'].includes(value)
    },
    chartData: {
      type: Object,
      required: true
    },
    chartOptions: {
      type: Object,
      default: () => ({})
    },
    chartPlugins: {
      type: Array,
      default: () => []
    },
    // Canvas properties
    width: {
      type: [Number, String],
      default: 400
    },
    height: {
      type: [Number, String],
      default: 300
    },
    chartClass: {
      type: String,
      default: ''
    },
    // Chart lifecycle
    autoUpdate: {
      type: Boolean,
      default: true
    },
    responsive: {
      type: Boolean,
      default: true
    },
    maintainAspectRatio: {
      type: Boolean,
      default: false
    },
    chartRef: {
      type: String,
      default: 'chartCanvas'
    }
  },
  data() {
    return {
      chart: null,
      isDestroyed: false,
      _updatingData: false,
      _updatingOptions: false,
      _cachedMergedOptions: null,
      _errorState: false,
      _lastErrorTime: 0,
      _initPromise: null,
      debouncedUpdate: null
    }
  },
  computed: {
    // Default options that can be overridden
    defaultOptions() {
      return {
        responsive: this.responsive,
        maintainAspectRatio: this.maintainAspectRatio,
        animation: {
          duration: 0,
          delay: 0
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        },
        plugins: {
          tooltip: {
            enabled: false
          }
        }
      }
    },
    
    // Merged options with defaults
    mergedOptions() {
      // Only merge if chartOptions exists and is different from last merge
      if (!this.chartOptions) {
        return this.defaultOptions
      }
      
      // Cache the merged options to prevent unnecessary recalculations
      if (!this._cachedMergedOptions || this._cachedMergedOptions.source !== this.chartOptions) {
        this._cachedMergedOptions = {
          source: this.chartOptions,
          result: this.createNonReactiveOptions(this.deepMerge(this.defaultOptions, this.chartOptions))
        }
      }
      
      return this._cachedMergedOptions.result
    }
  },
  // Watchers removed to prevent infinite loops - use explicit update methods instead
  beforeUnmount() {
    // Reset update flags and cache
    this._updatingData = false
    this._updatingOptions = false
    this._cachedMergedOptions = null
    
    // Clean up any pending initialization
    if (this._initPromise) {
      console.log('📊 CurveResultsBaseChart: Cleaning up pending initialization')
      this._initPromise = null
    }
    
    // Clean up debounced update
    if (this.debouncedUpdate) {
      clearTimeout(this.debouncedUpdate)
      this.debouncedUpdate = null
    }
  },
  methods: {
    // Deep merge utility
    deepMerge(target, source) {
      const result = { ...target }
      
      for (const key in source) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
          result[key] = this.deepMerge(result[key] || {}, source[key])
        } else {
          result[key] = source[key]
        }
      }
      
      return result
    },
    
    // Create a deep copy of chart data to break reactive references
    createChartDataCopy(data) {
      if (!data || typeof data !== 'object') {
        return data
      }
      
      try {
        // Use JSON.parse(JSON.stringify()) to create a completely non-reactive deep copy
        // This breaks ALL Vue reactivity and prevents infinite loops
        const copy = JSON.parse(JSON.stringify(data))
        console.log('📊 CurveResultsBaseChart: Created non-reactive chart data copy')
        return copy
      } catch (error) {
        console.warn('📊 CurveResultsBaseChart: Error creating chart data copy, using original:', error)
        return data
      }
    },
    
    // Initialize the chart with queue system to prevent conflicts
    async initializeChart() {
      // If already initializing, wait for current operation to complete
      if (this._initPromise) {
        console.log('📊 CurveResultsBaseChart: Already initializing, waiting for completion...')
        return await this._initPromise
      }
      
      // Create a promise for this initialization
      this._initPromise = this._performChartInitialization()
      
      try {
        const result = await this._initPromise
        return result
      } finally {
        this._initPromise = null
      }
    },
    
    // Perform the actual chart initialization with queue system
    async _performChartInitialization() {
      // Add this chart to the queue
      const queueId = Date.now() + Math.random()
      this.$options.static.chartInitQueue.push(queueId)
      
      // Wait for our turn in the queue
      while (this.$options.static.chartInitQueue[0] !== queueId) {
        await new Promise(resolve => setTimeout(resolve, 50)) // Wait 50ms before checking again
      }
      
      // Mark as initializing
      this.$options.static.isInitializing = true
      
      try {
        // Clean up any existing chart
        await this.destroyChart()
        
        // Get canvas element
        const canvas = this.$refs[this.chartRef]
        if (!canvas) {
          console.error('📊 CurveResultsBaseChart: Canvas element not found')
          return false
        }
        
        // Check if canvas is already in use by another chart instance
        if (canvas.chart && canvas.chart !== this.chart) {
          console.log('📊 CurveResultsBaseChart: Canvas already has chart, destroying it first')
          try {
            canvas.chart.destroy()
            canvas.chart = null
          } catch (error) {
            console.warn('📊 CurveResultsBaseChart: Error destroying existing chart on canvas:', error)
            // Force cleanup
            canvas.chart = null
          }
        }
        
        // Get 2D context
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          console.error('📊 CurveResultsBaseChart: 2D context not available')
          return false
        }
        
        // Validate chart data
        if (!this.validateChartData(this.chartData)) {
          console.error('📊 CurveResultsBaseChart: Invalid chart data')
          return false
        }
        
        // Only create chart if we have actual data
        if (!this.chartData || !this.chartData.datasets || this.chartData.datasets.length === 0) {
          console.log('📊 CurveResultsBaseChart: No data available yet, skipping chart creation')
          return false
        }
        
        console.log('📊 CurveResultsBaseChart: Creating chart instance with', this.chartData.datasets.length, 'datasets...')
        // Create new chart instance with non-reactive options and data copy
        this.chart = new Chart(ctx, {
          type: this.chartType,
          data: this.createChartDataCopy(this.chartData),
          options: this.createNonReactiveOptions(this.mergedOptions),
          plugins: Array.isArray(this.chartPlugins) ? this.chartPlugins : []
        })
        
        // Store reference to chart on canvas for cleanup
        canvas.chart = this.chart
        
        this.isDestroyed = false
        console.log('📊 CurveResultsBaseChart: Chart instance created successfully')
        
        // Emit ready event
        this.$emit('chart-ready', this.chart)
        
        return true
      } catch (error) {
        console.error('📊 CurveResultsBaseChart: Error initializing chart:', error)
        this.$emit('chart-error', error)
        return false
      } finally {
        // Remove from queue and mark as not initializing
        const index = this.$options.static.chartInitQueue.indexOf(queueId)
        if (index > -1) {
          this.$options.static.chartInitQueue.splice(index, 1)
        }
        this.$options.static.isInitializing = false
        console.log('📊 CurveResultsBaseChart: Removed from queue, remaining:', this.$options.static.chartInitQueue.length)
      }
    },
    
    // Validate chart data structure
    validateChartData(data) {
      if (!data || typeof data !== 'object') {
        console.log('📊 CurveResultsBaseChart: validateChartData failed - no data or not object')
        return false
      }
      
      if (!Array.isArray(data.datasets)) {
        console.log('📊 CurveResultsBaseChart: validateChartData failed - no datasets array')
        return false
      }
      
      // Check if datasets have required properties
      for (const dataset of data.datasets) {
        if (!dataset.label || !Array.isArray(dataset.data)) {
          console.log('📊 CurveResultsBaseChart: validateChartData failed - invalid dataset:', {
            hasLabel: !!dataset.label,
            hasData: !!dataset.data,
            isDataArray: Array.isArray(dataset.data)
          })
          return false
        }
      }
      
      console.log('📊 CurveResultsBaseChart: validateChartData passed')
      return true
    },
    
    // Create completely non-reactive chart options using Object.create(null)
    createNonReactiveOptions(options) {
      if (!options || typeof options !== 'object') {
        return options
      }
      
      try {
        // Use Object.create(null) to create an object with NO prototype chain
        // This completely bypasses Vue's reactivity system
        const isolatedOptions = Object.create(null)
        
        // Copy only the properties that Chart.js needs, avoiding any reactive references
        if (options.responsive !== undefined) isolatedOptions.responsive = options.responsive
        if (options.maintainAspectRatio !== undefined) isolatedOptions.maintainAspectRatio = options.maintainAspectRatio
        if (options.animation !== undefined) {
          isolatedOptions.animation = Object.create(null)
          if (options.animation.duration !== undefined) isolatedOptions.animation.duration = options.animation.duration
          if (options.animation.delay !== undefined) isolatedOptions.animation.delay = options.animation.delay
          if (options.animation.easing !== undefined) isolatedOptions.animation.easing = options.animation.easing
        }
        
        // Handle plugins - create a completely new plugins object with no prototype
        if (options.plugins) {
          isolatedOptions.plugins = Object.create(null)
          
          // Copy legend plugin
          if (options.plugins.legend) {
            const srcLegend = options.plugins.legend
            isolatedOptions.plugins.legend = Object.create(null)
            if (srcLegend.display !== undefined) isolatedOptions.plugins.legend.display = srcLegend.display
            if (srcLegend.position !== undefined) isolatedOptions.plugins.legend.position = srcLegend.position
            if (srcLegend.align !== undefined) isolatedOptions.plugins.legend.align = srcLegend.align
            if (srcLegend.maxHeight !== undefined) isolatedOptions.plugins.legend.maxHeight = srcLegend.maxHeight
            if (srcLegend.maxWidth !== undefined) isolatedOptions.plugins.legend.maxWidth = srcLegend.maxWidth
            if (srcLegend.fullSize !== undefined) isolatedOptions.plugins.legend.fullSize = srcLegend.fullSize
            if (srcLegend.reverse !== undefined) isolatedOptions.plugins.legend.reverse = srcLegend.reverse
            if (typeof srcLegend.onClick === 'function') isolatedOptions.plugins.legend.onClick = srcLegend.onClick
            if (typeof srcLegend.onHover === 'function') isolatedOptions.plugins.legend.onHover = srcLegend.onHover
            if (typeof srcLegend.onLeave === 'function') isolatedOptions.plugins.legend.onLeave = srcLegend.onLeave
          }
          
          // Copy tooltip plugin with external function support for curve selection
          if (options.plugins.tooltip) {
            isolatedOptions.plugins.tooltip = Object.create(null)
            if (options.plugins.tooltip.enabled !== undefined) isolatedOptions.plugins.tooltip.enabled = options.plugins.tooltip.enabled
            if (options.plugins.tooltip.mode !== undefined) isolatedOptions.plugins.tooltip.mode = options.plugins.tooltip.mode
            if (options.plugins.tooltip.intersect !== undefined) isolatedOptions.plugins.tooltip.intersect = options.plugins.tooltip.intersect
            if (typeof options.plugins.tooltip.external === 'function') isolatedOptions.plugins.tooltip.external = options.plugins.tooltip.external
          }
        }
        
        // Handle scales - create completely new scale objects with no prototype
        if (options.scales) {
          isolatedOptions.scales = Object.create(null)
          for (const [scaleKey, srcScale] of Object.entries(options.scales)) {
            const dstScale = Object.create(null)
            if (srcScale.type !== undefined) dstScale.type = srcScale.type
            if (srcScale.display !== undefined) dstScale.display = srcScale.display
            if (srcScale.position !== undefined) dstScale.position = srcScale.position
            if (srcScale.min !== undefined) dstScale.min = srcScale.min
            if (srcScale.max !== undefined) dstScale.max = srcScale.max
            if (srcScale.beginAtZero !== undefined) dstScale.beginAtZero = srcScale.beginAtZero
            if (srcScale.stacked !== undefined) dstScale.stacked = srcScale.stacked
            
            if (srcScale.title) {
              dstScale.title = Object.create(null)
              if (srcScale.title.display !== undefined) dstScale.title.display = srcScale.title.display
              if (srcScale.title.text !== undefined) dstScale.title.text = srcScale.title.text
            }
            
            if (srcScale.grid) {
              dstScale.grid = Object.create(null)
              if (srcScale.grid.display !== undefined) dstScale.grid.display = srcScale.grid.display
              if (srcScale.grid.color !== undefined) dstScale.grid.color = srcScale.grid.color
              if (srcScale.grid.drawOnChartArea !== undefined) dstScale.grid.drawOnChartArea = srcScale.grid.drawOnChartArea
            }
            
            if (srcScale.ticks) {
              dstScale.ticks = Object.create(null)
              if (typeof srcScale.ticks.callback === 'function') dstScale.ticks.callback = srcScale.ticks.callback
              if (srcScale.ticks.stepSize !== undefined) dstScale.ticks.stepSize = srcScale.ticks.stepSize
            }
            
            if (srcScale.time) {
              dstScale.time = Object.create(null)
              if (srcScale.time.unit !== undefined) dstScale.time.unit = srcScale.time.unit
              if (typeof srcScale.time.parser === 'function') dstScale.time.parser = srcScale.time.parser
              if (srcScale.time.displayFormats !== undefined) dstScale.time.displayFormats = srcScale.time.displayFormats
            }
            
            isolatedOptions.scales[scaleKey] = dstScale
          }
        }
        
        // Copy any other top-level properties that might be needed
        if (options.elements !== undefined) isolatedOptions.elements = options.elements
        if (options.layout !== undefined) isolatedOptions.layout = options.layout
        if (options.interaction !== undefined) isolatedOptions.interaction = options.interaction
        // Support for curve selection tooltips and interactions
        if (typeof options.onHover === 'function') isolatedOptions.onHover = options.onHover
        if (typeof options.onClick === 'function') isolatedOptions.onClick = options.onClick
        
        return isolatedOptions
      } catch (error) {
        console.warn('📊 CurveResultsBaseChart: Error creating non-reactive options, using fallback:', error)
        // Fallback: create a minimal non-reactive options object with no prototype
        return Object.assign(Object.create(null), {
          responsive: true,
          maintainAspectRatio: false,
          animation: Object.assign(Object.create(null), { duration: 0 })
        })
      }
    },
    
    // Destroy chart safely
    async destroyChart() {
      // Clear any pending timeouts
      if (this.debouncedUpdate) {
        clearTimeout(this.debouncedUpdate)
        this.debouncedUpdate = null
      }
      
      // Reset update flags and cache
      this._updatingData = false
      this._updatingOptions = false
      this._cachedMergedOptions = null
      
      if (!this.chart) return
      
      try {
        // Stop any ongoing operations
        if (this.chart.stop) {
          this.chart.stop()
        }
        
        // Disable animations and responsive updates
        if (this.chart.options) {
          if (this.chart.options.animation) {
            this.chart.options.animation.duration = 0
            this.chart.options.animation.delay = 0
          }
          if (this.chart.options.responsive) {
            this.chart.options.responsive = false
          }
        }
        
        // Destroy the chart
        this.chart.destroy()
        console.log('📊 CurveResultsBaseChart: Chart destroyed successfully')
        
        // Clear canvas reference
        const canvas = this.$refs[this.chartRef]
        if (canvas && canvas.chart === this.chart) {
          canvas.chart = null
        }
        
      } catch (error) {
        console.warn('📊 CurveResultsBaseChart: Error destroying chart:', error)
        
        // Force cleanup even if destroy fails
        try {
          // Clear canvas reference
          const canvas = this.$refs[this.chartRef]
          if (canvas && canvas.chart === this.chart) {
            canvas.chart = null
          }
        } catch (cleanupError) {
          console.warn('📊 CurveResultsBaseChart: Error during canvas cleanup:', cleanupError)
        }
      } finally {
        // Always clear the chart reference
        this.chart = null
        this.isDestroyed = true
      }
    },
    
    // Get chart instance (for external access)
    getChart() {
      return this.chart
    },
    
    // Check if chart is ready
    isReady() {
      return this.chart && !this.isDestroyed
    },
    
    // Check if chart is healthy (exists and not destroyed)
    isChartHealthy() {
      return this.chart && !this.chart.destroyed && !this.isDestroyed
    }
  },
  
  async mounted() {
    // Only initialize chart if we have data, otherwise wait for explicit initialization call
    if (this.chartData && this.chartData.datasets && this.chartData.datasets.length > 0) {
      console.log('📊 CurveResultsBaseChart: Data available on mount, initializing chart')
      await this.initializeChart()
    } else {
      console.log('📊 CurveResultsBaseChart: No data on mount, waiting for explicit initialization call')
      // Note: Watchers removed - chart will be initialized via explicit updateChart() calls
    }
  },
  
  // Add beforeUpdate hook to ensure chart cleanup before re-rendering
  beforeUpdate() {
    // If we're about to update and have a chart, ensure it's properly cleaned up
    if (this.chart && !this.isDestroyed) {
      try {
        // Check if chart is still valid
        if (!this.isChartHealthy()) {
          console.log('📊 CurveResultsBaseChart: Chart unhealthy before update, destroying...')
          this.destroyChart()
        }
      } catch (error) {
        console.warn('📊 CurveResultsBaseChart: Error checking chart health before update:', error)
        this.destroyChart()
      }
    }
  },

  beforeUnmount() {
    // Clean up chart when component is unmounted
    this.destroyChart()
  }
}
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.chart-container canvas {
  display: block;
  max-width: 100%;
  height: auto;
}
</style>
